import { FC, ReactNode } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import {
  color,
  ColorProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
} from "styled-system";

import Box from "../Box/Box";
import Icon from "../Icon/Icon";
import Typography from "../Typography/Typography";
import { useAccordionDispatch, useAccordionState } from "./AccordionContext";

interface AccordionHeaderProps {
  /** Additional content to be displayed inside header */
  children: ReactNode;
  /**
  Applies the theme typography styles and renders the corresponding html tags as follow:
{
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  body: "p",
  body-medium: "p",
  caption: "p",
  label: "span"
}
  */
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "body"
    | "body-medium"
    | "body-lg"
    | "caption"
    | "label";
  id: string;
}

const StyledHeading = styled(Typography)<LayoutProps>`
  ${layout}
`;
const HeadingButton = styled.button<LayoutProps & ColorProps & SpaceProps>`
  ${layout}
  ${color}
  ${space}
  ${css({
    color: "black",
    fontWeight: "semiBold",
    "&:hover": {
      color: "text-color-hover",
      "*": {
        color: "text-color-hover",
        fill: "text-color-hover",
      },
    },
    "&:focus": {
      color: "text-color-hover",
      outline: "none",
      "*": {
        color: "text-color-hover",
        fill: "text-color-hover",
        outline: "none",
      },
    },
  })}
  border: none;
  text-align: left;
  font-size: 16px;
  display: flex;
  align-items: center;
`;

export const AnimatedIconContainer = styled(Box)<PositionProps>`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);

  .collapsed {
    transition: transform 0.3s;
    transform: rotate(0deg);
  }
  .expanded {
    transition: transform 0.3s;
    transform: rotate(-180deg);
  }
  ${position}
`;

const AccordionHeader: FC<AccordionHeaderProps> = ({
  children,
  variant = "h6",
  id,
}: AccordionHeaderProps) => {
  const activeId = useAccordionState();
  const setActiveId = useAccordionDispatch();
  const isExpanded = activeId === id;

  return (
    <StyledHeading
      width="100%"
      m={0}
      p={0}
      variant={variant}
      css={`
        position: relative;
      `}
    >
      <HeadingButton
        width="100%"
        height="100%"
        p={0}
        py={6}
        pr={4}
        bg="transparent"
        aria-expanded={isExpanded}
        id={id}
        onClick={() => {
          if (isExpanded) {
            setActiveId(null);
            return;
          }
          setActiveId(id);
        }}
      >
        {children}
        <AnimatedIconContainer width={25} height={25}>
          <Icon
            className={isExpanded ? "expanded" : "collapsed"}
            aria-hidden={true}
            name="angle-down"
            weight="regular"
            color="black"
            width={25}
            height={25}
          />
        </AnimatedIconContainer>
      </HeadingButton>
    </StyledHeading>
  );
};
export default AccordionHeader;
